import {
  Product,
  ListProductsResponse,
  Channel,
  ListChannelsResponse,
  ListVersionsResponse,
  Version,
} from "@xboxstudios/hermes-apis/xboxstudios/hermes/product/v1/product_registry_pb";
import {
  useQuery,
  useQueryClient,
  useMutation,
  UseMutationOptions,
  useInfiniteQuery,
  keepPreviousData,
} from "@tanstack/react-query";
import { channelKeys, productKeys, versionKeys } from "./query-keys";
import {
  getProduct,
  listProducts,
  uploadMediaAsset,
  getChannel,
  listChannels,
  getVersion,
  listVersions,
} from "@utils/hermes-api/product";
import { gRPCError, ListQueryOptions } from "@utils/hermes-api/types";
import {
  getOrgNameFromResource,
  getProductNameFromResource,
} from "@utils/hermes-api/names";
import { Sort, QueryHookOptions, InfiniteQueryHookOptions } from "./utils";

export const useProductQuery = (
  productName: string,
  queryOptions: QueryHookOptions<Product, gRPCError> = {}
) => {
  return useQuery({
    queryKey: productKeys.single(productName),
    queryFn: () => getProduct(productName),
    ...queryOptions,
  });
};

export interface ProductSort extends Sort<"name"> {}

export const sortProducts = (
  products: Product[],
  { sortField = "name", asc = true }: ProductSort
) => {
  switch (sortField) {
    case "name":
      return [...products].sort((a, b) => {
        const aName = a.getDisplayName() || a.getId();
        const bName = b.getDisplayName() || b.getId();
        return aName.localeCompare(bName) * (asc ? 1 : -1);
      });
    default:
      console.warn(`Unknown product sort field: ${sortField}`);
      return products;
  }
};

export const useProductListQuery = (
  orgName: string,
  filters: ListQueryOptions = {},
  queryOptions: QueryHookOptions<ListProductsResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: productKeys.listFilter(orgName, filters),
    ...queryOptions,
    queryFn: async () => {
      const res = await listProducts(orgName, filters);
      // cache each product individually
      for (const p of res.getProductsList()) {
        queryClient.setQueryData(productKeys.single(p.getName()), p);
      }
      const { sortField, asc } = filters;
      // Locally sort products — TODO: remove when server-side sort is available
      res.setProductsList(
        sortProducts(res.getProductsList(), { sortField, asc })
      );
      return res;
    },
  });
};

export const useProductInfiniteListQuery = (
  orgName: string,
  filters: ListQueryOptions = {},
  queryOptions: InfiniteQueryHookOptions<ListProductsResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: productKeys.infiniteListFiltered(orgName, filters),
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const res = await listProducts(orgName, {
        ...filters,
        pageToken: pageParam as string,
      });

      // cache each product individually
      res
        .getProductsList()
        .forEach((p) =>
          queryClient.setQueryData(productKeys.single(p.getName()), p)
        );

      const { sortField, asc } = filters;
      if (sortField || asc !== undefined) {
        res.setProductsList(
          sortProducts(res.getProductsList(), { sortField, asc })
        );
      }

      return res;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.getNextPageToken() || undefined,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
};

export const useProductMutation = <TVariables>(
  mutateOptions: UseMutationOptions<Product, gRPCError, TVariables> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<Product, gRPCError, TVariables>({
    ...mutateOptions,
    onSuccess: (product, variables, context, meta) => {
      queryClient.setQueryData(productKeys.single(product.getName()), product);

      const orgName = product.getOrgName();
      // set/update individual cached product
      queryClient.invalidateQueries({
        queryKey: productKeys.list(orgName),
        exact: false,
      });

      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(product, variables, context, meta);
      }
    },
  });
};

export const useProductMediaMutation = (
  mutateOptions: UseMutationOptions<
    any,
    gRPCError,
    Parameters<typeof uploadMediaAsset>
  > = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<any, gRPCError, Parameters<typeof uploadMediaAsset>>({
    ...mutateOptions,
    mutationFn: (args) => uploadMediaAsset(...args),
    onSuccess: (res, [media, assetType, productName], context, meta) => {
      const orgName = getOrgNameFromResource(productName);
      queryClient.invalidateQueries({
        queryKey: productKeys.single(productName),
      });
      // set/update individual cached product
      queryClient.invalidateQueries({
        queryKey: productKeys.list(orgName),
        exact: false,
      });

      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(
          res,
          [media, assetType, productName],
          context,
          meta
        );
      }
    },
  });
};

export const useChannelQuery = (
  channelName: string,
  queryOptions: QueryHookOptions<Channel, gRPCError> = {}
) => {
  return useQuery({
    queryKey: channelKeys.single(channelName),
    queryFn: () => getChannel(channelName),
    ...queryOptions,
  });
};

export interface ChannelSort extends Sort<"name"> {}

export const sortChannels = (
  channels: Channel[],
  { sortField = "name", asc = true }: ChannelSort
) => {
  switch (sortField) {
    case "name":
      return [...channels].sort((a, b) => {
        const aName = a.getDisplayName() || a.getId();
        const bName = b.getDisplayName() || b.getId();
        return aName.localeCompare(bName) * (asc ? 1 : -1);
      });
    default:
      console.warn(`Unknown channel sort field: ${sortField}`);
      return channels;
  }
};

export const useChannelListQuery = (
  productName: string,
  queryOptions: QueryHookOptions<ListChannelsResponse, gRPCError> = {},
  filters: ListQueryOptions = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = channelKeys.listFiltered(productName, filters);
  return useQuery({
    queryKey,
    ...queryOptions,
    queryFn: async () => {
      const res = await listChannels(productName, filters);
      // cache each channel individually
      for (const c of res.getChannelsList()) {
        queryClient.setQueryData(channelKeys.single(c.getName()), c);
      }
      const { sortField, asc } = filters;
      // Locally sort channels — TODO: remove when server-side sort is available
      res.setChannelsList(
        sortChannels(res.getChannelsList(), { sortField, asc })
      );
      return res;
    },
  });
};

export const useChannelInfiniteListQuery = (
  productName: string,
  filters: ListQueryOptions = {},
  queryOptions: InfiniteQueryHookOptions<ListChannelsResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: channelKeys.infiniteListFiltered(productName, filters),
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const res = await listChannels(productName, {
        ...filters,
        pageToken: pageParam as string,
      });

      // cache each channel individually
      res
        .getChannelsList()
        .forEach((c) =>
          queryClient.setQueryData(channelKeys.single(c.getName()), c)
        );

      const { sortField, asc } = filters;
      if (sortField || asc !== undefined) {
        res.setChannelsList(
          sortChannels(res.getChannelsList(), { sortField, asc })
        );
      }

      return res;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.getNextPageToken() || undefined,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
};

export const useChannelMutation = <TVariables>(
  mutationOptions: UseMutationOptions<Channel, gRPCError, TVariables>
) => {
  const queryClient = useQueryClient();
  return useMutation<Channel, gRPCError, TVariables>({
    ...mutationOptions,
    onSuccess: (channel, variables, context, meta) => {
      queryClient.setQueryData(channelKeys.single(channel.getName()), channel);

      const productName = channel.getProductName();
      queryClient.invalidateQueries({
        queryKey: channelKeys.list(productName),
        exact: false,
      });

      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess(channel, variables, context, meta);
      }
    },
  });
};

export const useVersionQuery = (
  versionName: string,
  queryOptions: QueryHookOptions<Version, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: versionKeys.single(versionName),
    queryFn: () => getVersion(versionName),
    initialData: () => {
      if (versionName) {
        return queryClient.getQueryData(
          versionKeys.list(getProductNameFromResource(versionName))
        );
      }
    },
    ...queryOptions,
  });
};

export type VersionSort = Sort<"createTime">;

export const sortVersions = (
  versions: Version[],
  { sortField, asc }: VersionSort
) => {
  switch (sortField) {
    case "createTime":
      return [...versions].sort((a, b) => {
        const aTime = a.getCreateTime()?.getSeconds() ?? -Infinity;
        const bTime = b.getCreateTime()?.getSeconds() ?? -Infinity;
        const comp = aTime - bTime;
        const ascFactor = !!asc ? 1 : -1;
        return comp * ascFactor;
      });
    default:
      console.warn(`Unknown version sort field: ${sortField}`);
      return versions;
  }
};

export const useVersionListQuery = (
  productName: string,
  queryOptions: QueryHookOptions<ListVersionsResponse, gRPCError> = {},
  filters: ListQueryOptions = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = versionKeys.listFiltered(productName, filters);
  return useQuery({
    queryKey,
    initialData: () => {
      if (productName) {
        return queryClient.getQueryData(versionKeys.list(productName));
      }
    },
    ...queryOptions,
    queryFn: async () => {
      const res = await listVersions(productName, filters);
      // cache each version individually
      for (const v of res.getVersionsList()) {
        queryClient.setQueryData(versionKeys.single(v.getName()), v);
      }
      // Locally sort versions — TODO: remove when server-side sort is available
      const { sortField, asc } = filters.sort || {};
      if (sortField) {
        res.setVersionsList(
          sortVersions(res.getVersionsList(), {
            sortField: sortField || "createTime",
            asc: asc ?? false,
          })
        );
      }
      return res;
    },
  });
};

export const sortVersionListByCreateTime = (res: ListVersionsResponse) => {
  const sortedVersions = res
    .getVersionsList()
    .sort(
      (a, b) =>
        (b.getCreateTime()?.getSeconds() || 0) -
        (a.getCreateTime()?.getSeconds() || 0)
    );
  res.setVersionsList(sortedVersions);
  return res;
};

export const useVersionMutation = <TVariables>(
  mutationOptions: UseMutationOptions<Version, gRPCError, TVariables> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<Version, gRPCError, TVariables>({
    ...mutationOptions,
    onSuccess: (version, variables, context, meta) => {
      queryClient.setQueryData(versionKeys.single(version.getName()), version);
      const productName = version.getProductName();
      queryClient.invalidateQueries({
        queryKey: versionKeys.list(productName),
        exact: false,
      });

      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess(version, variables, context, meta);
      }
    },
  });
};

export const useVersionInfiniteListQuery = (
  productName: string,
  filters: ListQueryOptions = {},
  queryOptions: InfiniteQueryHookOptions<ListVersionsResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: versionKeys.infiniteListFiltered(productName, filters),
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const res = await listVersions(productName, {
        ...filters,
        pageToken: pageParam as string,
      });

      // cache each version individually
      res
        .getVersionsList()
        .forEach((v) =>
          queryClient.setQueryData(versionKeys.single(v.getName()), v)
        );

      const { sortField, asc } = filters.sort || {};
      if (sortField) {
        res.setVersionsList(
          sortVersions(res.getVersionsList(), {
            sortField: sortField || "createTime",
            asc: asc ?? false,
          })
        );
      }

      return res;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.getNextPageToken() || undefined,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
};
